import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, Check, Clock, AlertTriangle, ExternalLink, Users, Building2, Sparkles, X, UserMinus, UserX } from '../icons/wp-icons';
import { toast } from 'react-hot-toast';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../context/AuthContext';
import { getSubscription, redirectToCheckout, redirectToCustomerPortal, checkDowngradeImpact, cancelSubscription, reactivateSubscription, previewPlanChange } from '../api/stripeApi';

const PLANS = [
  {
    id: 'STARTER',
    name: 'Starter',
    description: 'Perfekt für kleine Betriebe',
    features: [
      'Inhaber + 3 Mitarbeiter',
      'Bis zu 300 Kunden',
      '200 KI-Anfragen/Monat',
      'E-Mail Support'
    ],
    prices: { MONTHLY: 29, YEARLY: 290 },
    highlighted: false
  },
  {
    id: 'PROFESSIONAL',
    name: 'Professional',
    description: 'Für wachsende Unternehmen',
    features: [
      'Inhaber + 10 Mitarbeiter',
      'Bis zu 1.500 Kunden',
      '1.000 KI-Anfragen/Monat',
      'Prioritäts-Support',
      'Erweiterte Berichte'
    ],
    prices: { MONTHLY: 79, YEARLY: 790 },
    highlighted: true
  },
  {
    id: 'BUSINESS',
    name: 'Business',
    description: 'Für etablierte Unternehmen',
    features: [
      'Inhaber + 30 Mitarbeiter',
      'Unbegrenzte Kunden',
      '4.000 KI-Anfragen/Monat',
      'Premium-Support',
      'API-Zugang',
      'Individuelle Anpassungen'
    ],
    prices: { MONTHLY: 149, YEARLY: 1490 },
    highlighted: false
  }
];

function Abonnement() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [billingCycle, setBillingCycle] = useState('MONTHLY');
  const [upgrading, setUpgrading] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [downgradeInfo, setDowngradeInfo] = useState(null);
  const [showDowngradeWarning, setShowDowngradeWarning] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(null); // {planId, planName, currentPrice, newPrice, diff}
  const [showDowngradeBlockedModal, setShowDowngradeBlockedModal] = useState(null); // {blockingIssues, planName}
  const [kiLimitAcceptance, setKiLimitAcceptance] = useState(null); // {planId, kiLimitInfo, accepted}
  const [acceptedKiLimit, setAcceptedKiLimit] = useState(false);

  // Reset loading states when returning from Stripe portal (bfcache + visibility fix)
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        // Page was restored from bfcache - reset loading states
        setOpeningPortal(false);
        setUpgrading(false);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Page became visible again (user returned from Stripe portal)
        setOpeningPortal(false);
        setUpgrading(false);
      }
    };

    const handleFocus = () => {
      // Window regained focus (fallback for visibility change)
      setOpeningPortal(false);
      setUpgrading(false);
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    loadSubscription();

    // Reset loading states on initial mount
    setOpeningPortal(false);
    setUpgrading(false);

    // Check for success/cancel from Stripe redirect
    if (searchParams.get('success') === 'true') {
      toast.success('Zahlung erfolgreich! Ihr Abonnement ist jetzt aktiv.');
      // Remove query params
      window.history.replaceState({}, '', '/einstellungen/abonnement');

      // Nach erfolgreicher Zahlung: Prüfen ob Onboarding nötig ist
      if (user?.isOrganisationOwner && user?.organisation && !user.organisation.onboardingCompleted) {
        // Kurz warten damit Toast sichtbar ist, dann zum Onboarding
        setTimeout(() => {
          window.location.href = '/onboarding';
        }, 1500);
      }
    } else if (searchParams.get('canceled') === 'true') {
      toast.error('Zahlung abgebrochen. Sie können es jederzeit erneut versuchen.');
      window.history.replaceState({}, '', '/einstellungen/abonnement');
    }
  }, [searchParams, user]);

  const loadSubscription = async () => {
    try {
      const [subData, downgradeData] = await Promise.all([
        getSubscription(),
        checkDowngradeImpact()
      ]);
      setSubscription(subData);
      setDowngradeInfo(downgradeData);
      if (subData.billingCycle) {
        setBillingCycle(subData.billingCycle);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
      toast.error('Abonnement konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const [loadingPreview, setLoadingPreview] = useState(false);

  const handlePlanClick = async (planId) => {
    if (!user?.isOrganisationOwner) {
      toast.error('Nur der Inhaber kann das Abonnement ändern');
      return;
    }

    // Bei bestehendem Abo: Exakte Vorschau von Stripe holen
    if (subscription?.stripeSubscriptionId) {
      const currentPlan = PLANS.find(p => p.id === subscription.plan);
      const newPlan = PLANS.find(p => p.id === planId);
      const currentBillingCycle = subscription.billingCycle || 'MONTHLY';

      if (currentPlan && newPlan) {
        const currentPrice = currentPlan.prices[currentBillingCycle];
        const newPrice = newPlan.prices[billingCycle];
        const isCycleChange = currentBillingCycle !== billingCycle;
        const isUpgrade = newPrice > currentPrice;
        const isDowngrade = newPrice < currentPrice;

        // Bei Plan-Wechsel: Exakte Vorschau von Stripe abrufen
        if (isUpgrade || isDowngrade || isCycleChange) {
          setLoadingPreview(true);
          try {
            const preview = await previewPlanChange(planId, billingCycle);

            if (preview.samePlan) {
              toast('Sie sind bereits auf diesem Plan', { icon: 'ℹ️' });
              setLoadingPreview(false);
              return;
            }

            // Preise für die Anzeige
            let displayCurrentPrice = isCycleChange
              ? `${currentPrice.toFixed(2).replace('.', ',')} €/${currentBillingCycle === 'MONTHLY' ? 'Monat' : 'Jahr'}`
              : currentPrice.toFixed(2).replace('.', ',');
            let displayNewPrice = isCycleChange
              ? `${newPrice.toFixed(2).replace('.', ',')} €/${billingCycle === 'MONTHLY' ? 'Monat' : 'Jahr'}`
              : newPrice.toFixed(2).replace('.', ',');

            setShowUpgradeModal({
              planId,
              planName: newPlan.name,
              currentPlanName: currentPlan.name,
              currentPrice: displayCurrentPrice,
              newPrice: displayNewPrice,
              // EXAKTE Werte von Stripe:
              subtotal: preview.subtotal,
              customerBalance: preview.customerBalance,
              amountDue: preview.amountDue,
              isCredit: preview.isCredit,
              creditAmount: preview.creditAmount,
              lines: preview.lines,
              isCycleChange,
              isUpgrade,
              isDowngrade,
              newCycle: billingCycle
            });
          } catch (error) {
            console.error('Preview error:', error);
            toast.error('Vorschau konnte nicht geladen werden');
          } finally {
            setLoadingPreview(false);
          }
          return;
        }
      }
    }

    // Kein Plan-Wechsel oder kein bestehendes Abo - direkt fortfahren
    handleUpgrade(planId);
  };

  const handleUpgrade = async (planId, options = {}) => {
    setShowUpgradeModal(null);
    setKiLimitAcceptance(null);
    setUpgrading(true);
    try {
      const result = await redirectToCheckout(planId, billingCycle, options);

      // Bei direktem Upgrade (bestehendes Abo mit Proration)
      if (result?.upgraded) {
        toast.success(result.message || 'Plan wurde erfolgreich geändert');
        // Subscription neu laden
        await loadSubscription();
        setUpgrading(false);
        setAcceptedKiLimit(false);
      } else if (result?.success && result?.upgraded === false) {
        // Bereits auf diesem Plan
        toast(result.message || 'Sie sind bereits auf diesem Plan', { icon: 'ℹ️' });
        await loadSubscription();
        setUpgrading(false);
      }
      // Bei Checkout-Weiterleitung bleibt upgrading true bis Seite wechselt
    } catch (error) {
      console.error('Checkout error:', error);
      setUpgrading(false);

      const errorData = error.response?.data;

      // DOWNGRADE_BLOCKED: Mitarbeiter/Kunden müssen erst gelöscht werden
      if (errorData?.code === 'DOWNGRADE_BLOCKED') {
        const targetPlan = PLANS.find(p => p.id === planId);
        setShowDowngradeBlockedModal({
          planName: targetPlan?.name || planId,
          blockingIssues: errorData.blockingIssues,
          message: errorData.message
        });
        return;
      }

      // KI_LIMIT_ACCEPTANCE_REQUIRED: Nutzer muss akzeptieren
      if (errorData?.code === 'KI_LIMIT_ACCEPTANCE_REQUIRED') {
        const targetPlan = PLANS.find(p => p.id === planId);
        setKiLimitAcceptance({
          planId,
          planName: targetPlan?.name || planId,
          kiLimitInfo: errorData.kiLimitInfo
        });
        return;
      }

      toast.error(errorData?.error || 'Checkout konnte nicht gestartet werden');
    }
  };

  // KI-Limit akzeptiert - Downgrade mit Akzeptanz durchführen
  const handleAcceptKiLimitAndDowngrade = async () => {
    if (!kiLimitAcceptance) return;
    setKiLimitAcceptance(null);
    await handleUpgrade(kiLimitAcceptance.planId, { acceptKiLimitExceeded: true });
  };

  const handleOpenPortal = async () => {
    if (!user?.isOrganisationOwner) {
      toast.error('Nur der Inhaber kann das Kundenportal öffnen');
      return;
    }

    // Prüfen ob es Downgrade-Warnungen gibt
    const hasDowngradeWarnings = downgradeInfo?.planChecks &&
      Object.entries(downgradeInfo.planChecks).some(([planId, check]) =>
        planId !== downgradeInfo.currentPlan && check.hasIssues
      );

    if (hasDowngradeWarnings) {
      setShowDowngradeWarning(true);
      return;
    }

    proceedToPortal();
  };

  const proceedToPortal = async () => {
    setShowDowngradeWarning(false);
    setOpeningPortal(true);
    try {
      await redirectToCustomerPortal();
    } catch (error) {
      console.error('Portal error:', error);
      toast.error('Kundenportal konnte nicht geöffnet werden');
    } finally {
      // Always reset loading state after a short delay
      // This handles both new tab opening and same-tab redirect scenarios
      setTimeout(() => setOpeningPortal(false), 500);
    }
  };

  const handleCancel = async () => {
    setCanceling(true);
    try {
      const result = await cancelSubscription();
      toast.success('Abonnement wird zum Periodenende gekündigt');
      setShowCancelModal(false);
      // Subscription neu laden
      await loadSubscription();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(error.response?.data?.error || 'Kündigung fehlgeschlagen');
    } finally {
      setCanceling(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await reactivateSubscription();
      toast.success('Abonnement wurde reaktiviert');
      // Subscription neu laden
      await loadSubscription();
    } catch (error) {
      console.error('Reactivate error:', error);
      toast.error(error.response?.data?.error || 'Reaktivierung fehlgeschlagen');
    } finally {
      setReactivating(false);
    }
  };

  const getStatusBadge = () => {
    if (!subscription) return null;

    const statusConfig = {
      TRIAL: { color: 'bg-blue-100 text-blue-800', icon: Clock, text: 'Testphase' },
      ACTIVE: { color: 'bg-green-100 text-green-800', icon: Check, text: 'Aktiv' },
      PAST_DUE: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle, text: 'Zahlung ausstehend' },
      CANCELLED: { color: 'bg-red-100 text-red-800', icon: AlertTriangle, text: 'Gekündigt' },
      SUSPENDED: { color: 'bg-red-100 text-red-800', icon: AlertTriangle, text: 'Testphase abgelaufen' }
    };

    const config = statusConfig[subscription.planStatus] || statusConfig.TRIAL;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4" />
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <CreditCard className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
          <h1 className="text-2xl sm:text-3xl font-serif font-bold">Abonnement</h1>
        </div>

        {/* Trial abgelaufen Banner */}
        {subscription?.planStatus === 'SUSPENDED' && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-red-800">Ihre Testphase ist abgelaufen</h3>
                <p className="text-red-700 mt-1">
                  Um WerkPilot weiter zu nutzen, wählen Sie bitte einen der folgenden Pläne.
                  Alle Ihre Daten sind weiterhin gespeichert und stehen nach dem Upgrade sofort wieder zur Verfügung.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Current Status Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-xl font-semibold">
                  {subscription?.plan || 'STARTER'} Plan
                  {subscription?.billingCycle && subscription?.planStatus === 'ACTIVE' && (
                    <span className="text-base font-normal text-gray-500 ml-2">
                      ({subscription.billingCycle === 'YEARLY' ? 'Jährlich' : 'Monatlich'})
                    </span>
                  )}
                </h2>
                {getStatusBadge()}
              </div>

              {subscription?.planStatus === 'TRIAL' && subscription?.trialDaysRemaining !== null && (
                <p className="text-gray-600">
                  Noch <span className="font-semibold text-primary">{subscription.trialDaysRemaining} Tage</span> in der Testphase
                </p>
              )}

              {subscription?.subscription?.currentPeriodEnd && (
                <p className="text-gray-600">
                  Nächste Abrechnung: {new Date(subscription.subscription.currentPeriodEnd).toLocaleDateString('de-DE')}
                </p>
              )}

              {subscription?.subscription?.cancelAtPeriodEnd && (
                <p className="text-yellow-600 font-medium mt-1">
                  Kündigung zum Periodenende aktiv
                </p>
              )}
            </div>

            {subscription?.stripeCustomerId && user?.isOrganisationOwner && (
              <button
                onClick={handleOpenPortal}
                disabled={openingPortal}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {openingPortal ? (
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></span>
                ) : (
                  <ExternalLink className="w-4 h-4" />
                )}
                Rechnungen & Zahlungen
              </button>
            )}
          </div>

          {/* Usage Stats */}
          {subscription?.limits && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-500 mb-4">Nutzung</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Users className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Mitarbeiter <span className="text-xs text-gray-400">(+Inhaber)</span></p>
                    <p className="font-semibold">max. {subscription.limits.maxUsers}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Building2 className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Kunden</p>
                    <p className="font-semibold">max. {subscription.limits.maxKunden}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Sparkles className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">KI-Anfragen</p>
                    <p className="font-semibold">
                      {subscription.limits.usedKiAnfragen || 0} / {subscription.limits.maxKiAnfragen}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Kündigung / Reaktivierung */}
          {subscription?.planStatus === 'ACTIVE' && subscription?.stripeSubscriptionId && user?.isOrganisationOwner && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">
                    Sie möchten Ihr Abonnement nicht verlängern?
                  </p>
                </div>
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="px-4 py-2 text-red-600 border border-red-200 rounded-button hover:bg-red-50 transition text-sm font-medium"
                >
                  Abonnement kündigen
                </button>
              </div>
            </div>
          )}

          {subscription?.planStatus === 'CANCELLED' && user?.isOrganisationOwner && (() => {
            // Ermittle das Enddatum aus verschiedenen Quellen
            let endDate = null;
            if (subscription?.subscriptionEndsAt) {
              endDate = new Date(subscription.subscriptionEndsAt);
            } else if (subscription?.subscription?.currentPeriodEnd) {
              endDate = new Date(subscription.subscription.currentPeriodEnd);
            }

            const formattedDate = endDate && !isNaN(endDate.getTime())
              ? endDate.toLocaleDateString('de-DE')
              : 'Unbekannt';

            return (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-orange-800">
                    Ihr Abonnement endet am{' '}
                    <strong>{formattedDate}</strong>.
                    Bis dahin können Sie alle Funktionen weiter nutzen.
                  </p>
                  <button
                    onClick={handleReactivate}
                    disabled={reactivating}
                    className="mt-3 text-primary hover:underline text-sm font-medium disabled:opacity-50"
                  >
                    {reactivating ? 'Wird reaktiviert...' : 'Kündigung widerrufen'}
                  </button>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setBillingCycle('MONTHLY')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'MONTHLY'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monatlich
            </button>
            <button
              onClick={() => setBillingCycle('YEARLY')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'YEARLY'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Jährlich
              <span className="ml-1.5 text-xs text-green-600 font-semibold">-17%</span>
            </button>
          </div>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((plan) => {
            const isCurrentPlan = subscription?.plan === plan.id;
            const isCurrentBillingCycle = subscription?.billingCycle === billingCycle;
            // Nur "Aktueller Plan" wenn Plan UND Abrechnungszyklus übereinstimmen
            const isCurrentPlanActive = isCurrentPlan && isCurrentBillingCycle && subscription?.planStatus === 'ACTIVE';
            const isCurrentPlanTrial = isCurrentPlan && subscription?.planStatus === 'TRIAL';
            const price = plan.prices[billingCycle];

            // Button-Text bestimmen
            const getButtonText = () => {
              if (isCurrentPlanActive) return 'Aktueller Plan';
              if (isCurrentPlanTrial) return 'Jetzt aktivieren';
              return 'Auswählen';
            };

            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-xl border-2 p-6 ${
                  plan.highlighted
                    ? 'border-primary shadow-lg'
                    : 'border-gray-200'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Beliebt
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{plan.description}</p>

                  <div className="mt-4">
                    <span className="text-4xl font-bold text-gray-900">
                      {billingCycle === 'MONTHLY'
                        ? price.toFixed(2).replace('.', ',')
                        : (price / 12).toFixed(2).replace('.', ',')}
                    </span>
                    <span className="text-gray-500 ml-1">/Monat</span>
                    {billingCycle === 'YEARLY' && (
                      <div className="text-sm text-gray-500 mt-1">
                        {price.toFixed(2).replace('.', ',')} € /Jahr
                      </div>
                    )}
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {isCurrentPlanActive ? (
                  <button
                    disabled
                    className="w-full py-3 px-4 bg-gray-100 text-gray-500 rounded-button font-medium cursor-not-allowed"
                  >
                    Aktueller Plan
                  </button>
                ) : (
                  <button
                    onClick={() => handlePlanClick(plan.id)}
                    disabled={upgrading || !user?.isOrganisationOwner}
                    className={`w-full py-3 px-4 rounded-button font-medium transition disabled:opacity-50 disabled:cursor-not-allowed ${
                      isCurrentPlanTrial
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-primary text-white hover:bg-primary-dark'
                    }`}
                  >
                    {upgrading ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                        Weiterleitung...
                      </span>
                    ) : (
                      getButtonText()
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Owner Hint */}
        {!user?.isOrganisationOwner && (
          <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 text-sm">
              Nur der Inhaber der Organisation kann das Abonnement verwalten.
            </p>
          </div>
        )}

      </div>

      {/* Limit überschritten Warnung */}
      {downgradeInfo?.currentLimitExceeded?.any && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50">
          <div className="bg-red-50 border border-red-300 rounded-lg shadow-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-red-800">Limit überschritten</h4>
                <p className="text-sm text-red-700 mt-1">
                  Ihre aktuelle Nutzung übersteigt die Limits Ihres Plans.
                  {downgradeInfo.currentLimitExceeded.users && (
                    <span className="block mt-1">
                      • Mitarbeiter: {downgradeInfo.currentUsage.users} von max. {downgradeInfo.currentLimits.maxUsers}
                    </span>
                  )}
                  {downgradeInfo.currentLimitExceeded.kunden && (
                    <span className="block">
                      • Kunden: {downgradeInfo.currentUsage.kunden} von max. {downgradeInfo.currentLimits.maxKunden}
                    </span>
                  )}
                </p>
                <p className="text-xs text-red-600 mt-2">
                  Neue Einträge können nicht erstellt werden, bis Sie innerhalb der Limits sind.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plan-Wechsel-Bestätigungs-Modal (Upgrade & Downgrade) */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-lg ${showUpgradeModal.isDowngrade ? 'bg-orange-100' : 'bg-[#378ab2]/10'}`}>
                  <Sparkles className={`w-6 h-6 ${showUpgradeModal.isDowngrade ? 'text-orange-600' : 'text-[#378ab2]'}`} />
                </div>
                <h3 className="text-lg font-semibold">
                  {showUpgradeModal.isCycleChange
                    ? `Wechsel zu ${showUpgradeModal.planName} (${showUpgradeModal.newCycle === 'YEARLY' ? 'Jährlich' : 'Monatlich'})`
                    : showUpgradeModal.isDowngrade
                      ? `Downgrade auf ${showUpgradeModal.planName}`
                      : `Upgrade auf ${showUpgradeModal.planName}`
                  }
                </h3>
              </div>

              <p className="text-gray-600 mb-4">
                {showUpgradeModal.isCycleChange ? (
                  <>Sie wechseln zum <strong>{showUpgradeModal.planName}</strong> Plan mit <strong>{showUpgradeModal.newCycle === 'YEARLY' ? 'jährlicher' : 'monatlicher'}</strong> Abrechnung.</>
                ) : (
                  <>Sie wechseln von <strong>{showUpgradeModal.currentPlanName}</strong> zu <strong>{showUpgradeModal.planName}</strong>.</>
                )}
              </p>

              <div className="p-4 bg-gray-50 rounded-lg mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Aktueller Plan</span>
                  <span className="font-medium">{showUpgradeModal.currentPrice}{!showUpgradeModal.isCycleChange && ' €/Monat'}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Neuer Plan</span>
                  <span className="font-medium">{showUpgradeModal.newPrice}{!showUpgradeModal.isCycleChange && ' €/Monat'}</span>
                </div>
                <hr className="my-2" />

                {showUpgradeModal.isCredit ? (
                  <>
                    {/* Downgrade: Zeige Gutschrift die entsteht */}
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-green-600">Gutschrift</span>
                      <span className="font-medium text-green-600">+{showUpgradeModal.creditAmount.toFixed(2).replace('.', ',')} €</span>
                    </div>
                    {showUpgradeModal.customerBalance > 0 && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-green-600">Bestehendes Guthaben</span>
                        <span className="font-medium text-green-600">+{showUpgradeModal.customerBalance.toFixed(2).replace('.', ',')} €</span>
                      </div>
                    )}
                    <hr className="my-2" />
                    <div className="flex justify-between items-center">
                      <span className="text-gray-900 font-medium">Zu zahlen</span>
                      <span className="font-bold text-green-600">{showUpgradeModal.amountDue.toFixed(2).replace('.', ',')} €</span>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Upgrade: Zeige exakte Stripe-Berechnung */}
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">Proration</span>
                      <span className="font-medium">{showUpgradeModal.subtotal.toFixed(2).replace('.', ',')} €</span>
                    </div>
                    {showUpgradeModal.customerBalance > 0 && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-green-600">Ihr Guthaben</span>
                        <span className="font-medium text-green-600">-{showUpgradeModal.customerBalance.toFixed(2).replace('.', ',')} €</span>
                      </div>
                    )}
                    <hr className="my-2" />
                    <div className="flex justify-between items-center">
                      <span className="text-gray-900 font-medium">Zu zahlen</span>
                      <span className={`font-bold ${showUpgradeModal.amountDue > 0 ? 'text-[#378ab2]' : 'text-green-600'}`}>
                        {showUpgradeModal.amountDue.toFixed(2).replace('.', ',')} €
                      </span>
                    </div>
                  </>
                )}
              </div>

              <p className="text-sm text-gray-500 mb-4">
                {showUpgradeModal.isCredit ? (
                  <>Beim Downgrade wird Ihnen die ungenutzte Zeit als <strong>Guthaben</strong> gutgeschrieben. Dieses wird automatisch mit zukünftigen Rechnungen verrechnet.</>
                ) : (
                  <>Dieser Betrag wird <strong>sofort</strong> von Ihrer hinterlegten Zahlungsmethode abgebucht.</>
                )}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowUpgradeModal(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-button hover:bg-gray-50 transition font-medium"
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => handleUpgrade(showUpgradeModal.planId)}
                  disabled={upgrading}
                  className={`flex-1 px-4 py-2 text-white rounded-button transition font-medium disabled:opacity-50 ${
                    showUpgradeModal.isDowngrade
                      ? 'bg-orange-500 hover:bg-orange-600'
                      : 'bg-[#378ab2] hover:bg-[#2e7399]'
                  }`}
                >
                  {upgrading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                      Wechsel läuft...
                    </span>
                  ) : (
                    'Wechsel bestätigen'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kündigungs-Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold">Abonnement kündigen</h3>
              </div>

              <p className="text-gray-600 mb-4">
                Sind Sie sicher, dass Sie Ihr Abonnement kündigen möchten?
              </p>

              <div className="p-4 bg-gray-50 rounded-lg mb-6">
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Ihr Abonnement bleibt bis zum Ende der bezahlten Periode aktiv</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Sie können alle Funktionen bis dahin weiter nutzen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Sie können die Kündigung jederzeit widerrufen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                    <span>Nach Ablauf wird Ihr Account gesperrt</span>
                  </li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleCancel}
                  disabled={canceling}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium disabled:opacity-50"
                >
                  {canceling ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                      Wird gekündigt...
                    </span>
                  ) : (
                    'Kündigung bestätigen'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Downgrade Warnung Modal */}
      {showDowngradeWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold">Wichtiger Hinweis</h3>
                </div>
                <button
                  onClick={() => setShowDowngradeWarning(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg transition"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <p className="text-gray-600 mb-4">
                Bei einem Wechsel zu einem kleineren Plan könnten folgende Einschränkungen auftreten:
              </p>

              <div className="space-y-3 mb-6">
                {downgradeInfo?.planChecks && Object.entries(downgradeInfo.planChecks)
                  .filter(([planId, check]) => planId !== downgradeInfo.currentPlan && check.hasIssues)
                  .map(([planId, check]) => (
                    <div key={planId} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="font-medium text-orange-800 mb-2">
                        Bei Wechsel zu {planId}:
                      </div>
                      <ul className="space-y-1">
                        {check.issues.map((issue, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-orange-700">
                            {issue.type === 'users' ? (
                              <UserMinus className="w-4 h-4" />
                            ) : (
                              <Building2 className="w-4 h-4" />
                            )}
                            {issue.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
                }
              </div>

              <div className="p-3 bg-gray-50 rounded-lg mb-6">
                <p className="text-sm text-gray-600">
                  <strong>Hinweis:</strong> Bestehende Daten werden nicht gelöscht, aber Sie können keine neuen Einträge erstellen, bis die Nutzung unter dem neuen Limit liegt.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDowngradeWarning(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  Abbrechen
                </button>
                <button
                  onClick={proceedToPortal}
                  disabled={openingPortal}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition font-medium disabled:opacity-50"
                >
                  {openingPortal ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                      Öffne Portal...
                    </span>
                  ) : (
                    'Trotzdem fortfahren'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Downgrade Blockiert Modal - Mitarbeiter/Kunden müssen erst reduziert werden */}
      {showDowngradeBlockedModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <UserX className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold">Downgrade nicht möglich</h3>
                </div>
                <button
                  onClick={() => setShowDowngradeBlockedModal(null)}
                  className="p-1 hover:bg-gray-100 rounded-lg transition"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <p className="text-gray-600 mb-4">
                Der Wechsel zu <strong>{showDowngradeBlockedModal.planName}</strong> ist nicht möglich, da Ihre aktuelle Nutzung die Limits des Plans überschreitet:
              </p>

              <div className="space-y-3 mb-6">
                {showDowngradeBlockedModal.blockingIssues?.map((issue, idx) => (
                  <div key={idx} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      {issue.type === 'users' ? (
                        <UserMinus className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      ) : (
                        <Building2 className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      )}
                      <div>
                        <div className="font-medium text-red-800 mb-1">
                          {issue.type === 'users' ? 'Zu viele Mitarbeiter' : 'Zu viele Kunden'}
                        </div>
                        <p className="text-sm text-red-700">{issue.message}</p>
                        <div className="mt-2 text-xs text-red-600">
                          Aktuell: <strong>{issue.current}</strong> | Limit: <strong>{issue.limit}</strong> | Überschuss: <strong>{issue.excess}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
                <p className="text-sm text-blue-800">
                  <strong>So geht's:</strong> Gehen Sie zu{' '}
                  {showDowngradeBlockedModal.blockingIssues?.some(i => i.type === 'users') && (
                    <a href="/mitarbeiter" className="underline hover:text-blue-600">Mitarbeiter</a>
                  )}
                  {showDowngradeBlockedModal.blockingIssues?.some(i => i.type === 'users') && showDowngradeBlockedModal.blockingIssues?.some(i => i.type === 'kunden') && ' oder '}
                  {showDowngradeBlockedModal.blockingIssues?.some(i => i.type === 'kunden') && (
                    <a href="/kunden" className="underline hover:text-blue-600">Kunden</a>
                  )}
                  {' '}und reduzieren Sie die Anzahl, bevor Sie den Plan wechseln.
                </p>
              </div>

              <button
                onClick={() => setShowDowngradeBlockedModal(null)}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium"
              >
                Verstanden
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KI-Limit Akzeptanz Modal - Soft-Warnung, kann akzeptiert werden */}
      {kiLimitAcceptance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold">KI-Limit wird überschritten</h3>
                </div>
                <button
                  onClick={() => setKiLimitAcceptance(null)}
                  className="p-1 hover:bg-gray-100 rounded-lg transition"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg mb-4">
                <p className="text-orange-800">{kiLimitAcceptance.kiLimitInfo?.message}</p>
                <div className="mt-2 text-sm text-orange-600">
                  Genutzt: <strong>{kiLimitAcceptance.kiLimitInfo?.used}</strong> | Neues Limit: <strong>{kiLimitAcceptance.kiLimitInfo?.newLimit}</strong>
                </div>
              </div>

              <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg mb-6 cursor-pointer hover:bg-gray-100 transition">
                <input
                  type="checkbox"
                  checked={acceptedKiLimit}
                  onChange={(e) => setAcceptedKiLimit(e.target.checked)}
                  className="mt-1 w-4 h-4 text-orange-600 rounded border-gray-300 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">
                  Ich verstehe, dass ich nach dem Downgrade <strong>keine weiteren KI-Anfragen</strong> stellen kann, bis mein Kontingent im nächsten Abrechnungsmonat zurückgesetzt wird.
                </span>
              </label>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setKiLimitAcceptance(null);
                    setAcceptedKiLimit(false);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleAcceptKiLimitAndDowngrade}
                  disabled={!acceptedKiLimit || upgrading}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {upgrading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                      Wechsel läuft...
                    </span>
                  ) : (
                    `Zu ${kiLimitAcceptance.planName} wechseln`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Abonnement;
